import { dedupeAudioInputs, resolveMicChoice } from "./micDevices";
import * as Sentry from "@sentry/react";
import { loadMicChoice } from "./micPreference";

// Bounded to 250 ms so a hung enumerateDevices delays the start by at most that long.
const START_ENUMERATE_TIMEOUT_MS = 250;

// Prefer (ideal, not exact) the remembered mic if it is plugged in. With no saved choice, no match,
// failure, or timeout, return the caller's base constraints, or true: the browser's own default mic.
export const resolveStartAudioConstraints = async (
    base?: MediaTrackConstraints,
): Promise<true | MediaTrackConstraints> => {
    const fallback: true | MediaTrackConstraints = base ?? true;
    const choice = loadMicChoice();
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        const devices = await Promise.race([
            navigator.mediaDevices.enumerateDevices(),
            new Promise<null>((resolve) => {
                timer = setTimeout(() => resolve(null), START_ENUMERATE_TIMEOUT_MS);
            }),
        ]);
        if (!devices) return fallback;
        const match = resolveMicChoice(choice, dedupeAudioInputs(devices));
        return match && match.deviceId ? { ...base, deviceId: { ideal: match.deviceId } } : fallback;
    } catch {
        return fallback;
    } finally {
        if (timer) clearTimeout(timer);
    }
};

const RETRY_WITH_DEFAULT_ERRORS = ["NotReadableError", "AbortError", "OverconstrainedError"];

// A remembered mic that is plugged in but will not open (headset stuck in call mode, held exclusively)
// must not lock the clinic out: retry once on the caller's fallback constraints (the browser default unless the caller supplied constraints).
// Permission and missing-device errors are not retried; they are the same for every mic.
export const openMicStream = async (
    audio: true | MediaTrackConstraints,
    fallback: true | MediaTrackConstraints,
): Promise<MediaStream> => {
    try {
        return await navigator.mediaDevices.getUserMedia({ audio });
    } catch (err) {
        const name = err && typeof err === "object" ? (err as { name?: unknown }).name : undefined;
        const chosen = audio !== fallback && typeof audio === "object" && !!audio.deviceId;
        if (!chosen || typeof name !== "string" || RETRY_WITH_DEFAULT_ERRORS.indexOf(name) === -1) throw err;
        Sentry.addBreadcrumb({
            category: "recording",
            level: "warning",
            message: "recording.mic_choice_unusable",
            data: { name },
        });
        return navigator.mediaDevices.getUserMedia({ audio: fallback });
    }
};
